import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Modal } from '@spirit-testing/component-library';
import { TranslatePipe } from '@ngx-translate/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TestModuleService } from '../../../services/test-module.service';
import { TestModule } from '../../../models/test-module.model';

/**
 * Modul-Einbinden-Dialog (NEX-465, Design page-module.jsx:16-86): listet die
 * anderen Module des Projekts (eigenes Modul + bereits eingebundene werden
 * ausgeblendet), erlaubt Suche + Mehrfachauswahl und gibt die gewaehlten
 * Modul-IDs via {@link applied} zurueck.
 */
@Component({
  selector: 'app-module-embed-dialog',
  imports: [Modal, TranslatePipe, MatFormField, MatLabel, MatInput, MatCheckbox, MatProgressSpinner],
  templateUrl: './module-embed-dialog.component.html',
  styleUrl: './module-embed-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleEmbedDialogComponent implements AfterViewInit {
  private readonly moduleService = inject(TestModuleService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private loaded = false;

  readonly projectId = input.required<string>();
  /** IDs, die nicht wählbar sein dürfen: eigenes Modul + bereits eingebundene. */
  readonly excludeIds = input<readonly string[]>([]);
  readonly visible = model(false);

  readonly applied = output<TestModule[]>();

  @ViewChild('embedContent') embedContentTemplate!: TemplateRef<unknown>;
  resolvedTemplate: TemplateRef<unknown> | null = null;

  readonly modules = signal<TestModule[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly searchTerm = signal('');
  readonly selectedIds = signal<ReadonlySet<string>>(new Set());

  readonly filtered = computed(() => {
    const exclude = new Set(this.excludeIds());
    const term = this.searchTerm().toLowerCase().trim();
    return this.modules()
      .filter((m) => !exclude.has(m.id))
      .filter((m) => !term || m.name.toLowerCase().includes(term) || m.key.toLowerCase().includes(term));
  });

  readonly canApply = computed(() => this.selectedIds().size > 0);

  constructor() {
    effect(() => {
      if (this.visible() && !this.loaded) {
        this.loaded = true;
        this.loadModules();
      }
    });
  }

  ngAfterViewInit(): void {
    this.resolvedTemplate = this.embedContentTemplate;
    this.cdr.detectChanges();
  }

  onSearch(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  isSelected(moduleId: string): boolean {
    return this.selectedIds().has(moduleId);
  }

  toggle(moduleId: string): void {
    this.selectedIds.update((current) => {
      const next = new Set(current);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  }

  onApply(): void {
    if (!this.canApply()) {
      return;
    }
    const selected = this.selectedIds();
    this.applied.emit(this.modules().filter((m) => selected.has(m.id)));
    this.close();
  }

  onCancel(): void {
    this.close();
  }

  private close(): void {
    this.visible.set(false);
    this.searchTerm.set('');
    this.selectedIds.set(new Set());
  }

  private loadModules(): void {
    this.loading.set(true);
    this.error.set(false);
    this.moduleService
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (modules) => {
          this.modules.set(modules);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }
}
